/**
 * Pure presentational config + helpers for the seller POS. No React, no state —
 * just the bits the views read (currency formatting, the top-bar action list,
 * the payment-method list aligned to the BosAPI `PaymentType` enum).
 */

import type { PosProduct, PosVariant } from './posProduct';

// Shared with Transactions and Shifts, so they live in `utils`.
export { formatDiscountValue, formatGel } from '@/utils';

/**
 * What tells a variant apart from its siblings, for the picker and the cart
 * line: its option values (`Black / L`). The API leaves a single-variant
 * product's label `null`, since its name already says it all, so `null` means
 * "show nothing". A several-variant row that somehow arrives unlabelled falls
 * back to its barcode, so two lines of one product never look the same.
 */
export const variantLabel = (
    product: PosProduct,
    variant: PosVariant,
): string | null =>
    variant.label ?? (product.variants.length > 1 ? variant.barcode : null);

/**
 * Top-bar action buttons. Each is a CTA only for now — the modals behind them
 * (shift, stock receive, returns, Z-report, …) are implemented in a later
 * phase, so pressing one just surfaces a "coming soon" toast.
 */
export type PosActionKey =
    | 'receive'
    | 'cash'
    | 'loyalty'
    | 'return'
    | 'alerts'
    | 'debtors'
    | 'zReport'
    | 'stocktake'
    | 'promotions'
    | 'reports';

export type PosAction = {
    key: PosActionKey;
    labelKey: string;
    icon: string;
    /**
     * Shown as a direct icon button in the top bar. The rest collapse into the
     * "More" menu (progressive disclosure) so the toolbar stays uncluttered.
     */
    primary?: boolean;
};

export const POS_ACTIONS: PosAction[] = [
    {
        key: 'return',
        labelKey: 'seller.actions.return',
        icon: 'solar:rewind-back-circle-bold-duotone',
        primary: true,
    },
    {
        key: 'loyalty',
        labelKey: 'seller.actions.loyalty',
        icon: 'solar:card-bold-duotone',
        primary: true,
    },
    {
        key: 'cash',
        labelKey: 'seller.actions.cash',
        icon: 'solar:safe-square-bold-duotone',
        primary: true,
    },
    {
        key: 'receive',
        labelKey: 'seller.actions.receive',
        icon: 'solar:inbox-in-bold-duotone',
    },
    {
        key: 'alerts',
        labelKey: 'seller.actions.alerts',
        icon: 'solar:bell-bold-duotone',
    },
    {
        key: 'debtors',
        labelKey: 'seller.actions.debtors',
        icon: 'solar:hand-money-bold-duotone',
    },
    {
        key: 'zReport',
        labelKey: 'seller.actions.zReport',
        icon: 'solar:document-text-bold-duotone',
    },
    {
        key: 'stocktake',
        labelKey: 'seller.actions.stocktake',
        icon: 'solar:clipboard-list-bold-duotone',
    },
    {
        key: 'promotions',
        labelKey: 'seller.actions.promotions',
        icon: 'solar:tag-bold-duotone',
    },
    {
        key: 'reports',
        labelKey: 'seller.actions.reports',
        icon: 'solar:chart-2-bold-duotone',
    },
];

export const POS_PRIMARY_ACTIONS = POS_ACTIONS.filter(
    (action) => action.primary,
);
export const POS_MORE_ACTIONS = POS_ACTIONS.filter((action) => !action.primary);
